import { Offer } from 'src/app/stores/stores';

export default class OfferUtils {
  static showPlusButton(
    offer: Offer,
    basketEnabled: boolean,
    orderingEnabled: boolean,
    isAdminOrderCapture: boolean = false,
    orderCaptureEnabled: boolean = true,
  ): boolean {
    const isOrderable = offer.isOrderable;
    const inStock = offer.inStock;
    let canOrder = false;
    if (isAdminOrderCapture) {
      canOrder = orderCaptureEnabled || orderingEnabled;
    } else {
      canOrder = basketEnabled || orderingEnabled;
    }
    if (isOrderable && inStock && canOrder) {
      return true;
    }
    return false;
  }

  /**
   * Handles admin order capture behavior for item clicks
   * This method centralizes the logic for admin order capture mode to avoid code duplication
   * @param orderCaptureEnabled - Whether order capture is enabled
   * @param itemData - The item data to process
   * @param basketEnabled - Whether basket is enabled
   * @param callbacks - Callback functions for different actions
   */
  static handleAdminOrderCapture(
    orderCaptureEnabled: boolean,
    itemData: {
      offerId: number;
      categoryId: number;
      inStock: boolean;
      isDirectlyOrderable: boolean;
      isExpandable: boolean;
      isOrderable: boolean;
    },
    basketEnabled: boolean,
    callbacks: {
      addToCart: (data: any) => void;
      showDetails: (
        offerId: number,
        categoryId: number,
        inStock: boolean,
        isExpandable: boolean,
        isOrderable: boolean,
        basketEnabled: boolean,
      ) => void;
    },
  ): void {
    if (orderCaptureEnabled) {
      callbacks.addToCart({
        ...itemData,
        basketEnabled,
      });
    } else {
      callbacks.showDetails(
        itemData.offerId,
        itemData.categoryId,
        itemData.inStock,
        itemData.isExpandable,
        itemData.isOrderable,
        basketEnabled,
      );
    }
  }

  /**
   * Decides whether a catalog tap should add the item directly to the basket
   * (true) or open the product detail page (false).
   *
   * In admin order capture mode the customer-facing storefront settings
   * (ADD_BASKET_ONE_CLICK / oneClickSetting and BASKET_ENABLED / basketEnabled)
   * are bypassed: staff capture must always one-click-add an option-less item
   * regardless of whether the store allows customer self-ordering. Outside
   * capture mode both settings apply, preserving the existing storefront
   * behaviour. The item / stock / ordering gates always apply.
   */
  static shouldAddDirectlyToBasket(params: {
    captureMode: boolean;
    oneClickSetting: boolean;
    basketEnabled: boolean;
    isDirectlyOrderable: boolean;
    isOrderable: boolean;
    inStock: boolean;
    closedForOrdering: boolean;
  }): boolean {
    const {
      captureMode,
      oneClickSetting,
      basketEnabled,
      isDirectlyOrderable,
      isOrderable,
      inStock,
      closedForOrdering,
    } = params;
    const oneClickEnabled = captureMode ? true : oneClickSetting;
    const basketUsable = captureMode ? true : basketEnabled;
    return (
      !!oneClickEnabled &&
      !!isDirectlyOrderable &&
      !!isOrderable &&
      !!basketUsable &&
      !!inStock &&
      !closedForOrdering
    );
  }

  /**
   * Check if single item per order mode is enabled for the store
   * @param store - The store object with settings
   * @returns boolean indicating if single item per order is enabled
   */
  static isSingleItemModeEnabled(store: any): boolean {
    return store?.settings?.SINGLE_PARENT_ITEM_PER_ORDER === 'true';
  }

  /**
   * Check if an item is a parent item
   * @param item - The item to check
   * @returns boolean indicating if the item is a parent item
   */
  static isParentItem(item: any): boolean {
    return item?.hierarchyLevel === 'PARENT';
  }

  /**
   * Check if quantity can be increased for an item in single item mode
   * @param store - The store object with settings
   * @param item - The item to check
   * @returns boolean indicating if quantity can be increased
   */
  static canIncreaseQuantity(store: any, item: any): boolean {
    if (!this.isSingleItemModeEnabled(store) || !this.isParentItem(item)) {
      return true;
    }
    // Use nullish coalescing to handle undefined/null quantities (catalog browsing)
    // Allow increase only if current quantity is less than 1
    return (item?.quantity ?? 0) < 1;
  }

  /**
   * Check if quantity should be forced to 1 for an item in single item mode
   * @param store - The store object with settings
   * @param item - The item to check
   * @returns boolean indicating if quantity should be forced to 1
   */
  static shouldForceQuantityToOne(store: any, item: any): boolean {
    return this.isSingleItemModeEnabled(store) && this.isParentItem(item);
  }

  /**
   * Check if quantity decrease should be prevented for an item in single item mode
   * @param store - The store object with settings
   * @param item - The item to check
   * @param currentQty - The current quantity
   * @returns boolean indicating if quantity decrease should be prevented
   */
  static shouldPreventQuantityDecrease(
    store: any,
    item: any,
    currentQty: number,
  ): boolean {
    return (
      this.isSingleItemModeEnabled(store) &&
      this.isParentItem(item) &&
      currentQty <= 1
    );
  }
}
